import {
  APPLICANT_LEVEL_MISSING_ERROR,
  APPLICANT_LEVEL_INVALID_ERROR,
  CAMPUS_MISSING_ERROR,
  CAMPUS_INVALID_ERROR,
  TERMS_MISSING_ERROR,
  TERMS_INVALID_ERROR,
  MAJOR_NAME_INVALID_ERROR,
  CATEGORY_INVALID_ERROR,
  SHOW_CLOSED_INVALID_ERROR
} from './errors';

import {
  FALL_TERM, SPRING_TERM, WINTER_TERM
} from './api';

const TERM_OPTIONS_FOR_RESULTS = {
  [FALL_TERM]: 'Fall',
  [SPRING_TERM]: 'Spring',
  [WINTER_TERM]: 'Winter'
};

const UCLA_DB_NAME = 'Los Angeles';
const UCLA_DISPLAY_NAME = 'UCLA';

export const losAngelesToUCLA = campusName => {
  return (campusName === UCLA_DB_NAME) ? UCLA_DISPLAY_NAME : campusName;
};

export const toSentenceCase = string => {
  let didFirstCap = false;

  const parts = string.split(/\s+/);
  const adjustedSentence = parts.map(part => {
    let word = part.toLowerCase();

    if (!didFirstCap) {
      const firstChar = part.charAt(0);
      const isNotNumber = Number.isNaN(parseInt(firstChar));
      // we want to start with the first letter of the first word that isn't a number
      if (isNotNumber) {
        word = firstChar.toUpperCase() + word.substr(1);
        didFirstCap = true;
      }
    }

    return word;
  });

  return adjustedSentence.join(' ');
};

export const getTermFromId = (years, yearId, termId) => {
  const term = TERM_OPTIONS_FOR_RESULTS[termId];
  if (!term) {
    return '';
  }

  const yearIdWithoutBrackets = yearId.slice(1, -1); // [21] -> 21
  const year = years[yearIdWithoutBrackets][termId];

  return `${term} ${year}`;
};

export const parseStatusFlags = (flagString, applicantLevelId) => {
  // split every three characters
  const groups = flagString.match(/.{1,3}/g);

  // we only need to match the first part of each group
  const levelStatuses = groups.map(group => group[0] === 'O');

  // IDs start at 1
  return levelStatuses[applicantLevelId - 1];
};

export const validateSearchFormParams = params => {
  if (typeof params !== 'object') {
    throw new Error('Please give me an object');
  }

  const errors = [];
  const { applicantLevel, campuses, terms, majorName, category, showClosed } = params;

  // applicant level
  if (!applicantLevel) {
    errors.push(APPLICANT_LEVEL_MISSING_ERROR);
  } else if (typeof applicantLevel !== 'number') {
    errors.push(APPLICANT_LEVEL_INVALID_ERROR);
  }

  // terms
  if (!terms) {
    errors.push(TERMS_MISSING_ERROR);
  }
  if (!Array.isArray(terms)) {
    errors.push(TERMS_INVALID_ERROR);
  } else if(terms.length === 0) {
    errors.push(TERMS_MISSING_ERROR);
  }

  // campuses
  if (!campuses) {
    errors.push(CAMPUS_MISSING_ERROR);
  }

  if (!Array.isArray(campuses)) {
    errors.push(CAMPUS_INVALID_ERROR);
  } else if (campuses.length === 0) {
    errors.push(CAMPUS_MISSING_ERROR);
  }

  // majorname
  if (majorName && (typeof majorName !== 'string')) {
    errors.push(MAJOR_NAME_INVALID_ERROR);
  }

  // category
  if (category && (typeof category !== 'number')) {
    errors.push(CATEGORY_INVALID_ERROR);
  }

  // showclosed
  if (showClosed && (typeof showClosed !== 'boolean')) {
    errors.push(SHOW_CLOSED_INVALID_ERROR);
  }

  return errors;
};

export const formatSearchParamsAsQueryString = params => {
  if (typeof params !== 'object') {
    throw new Error('Please give me an object');
  }

  const paramSegments = Object.keys(params)
    .map(key => ({param: key, value: params[key]}))
    .filter(param => typeof param.value !== 'undefined');
  const stringSegments = paramSegments.map(segment => {
    const { param, value } = segment;

    let valueString = `${value}`;
    if (Array.isArray(value)) {
      valueString = `[${value.join(',')}]`;
    }
    if (typeof value === 'string') {
      valueString = `"${value}"`;
    }

    return `${param}=${valueString}`;
  });

  return '?' + stringSegments.map(encodeURIComponent).join('&');
};

export const parseQueryString = queryString => {
  if (typeof queryString !== 'string') {
    throw new Error('Please give me a string');
  }

  if (queryString.charAt(0) !== '?') {
    throw new Error('Please give me a string starting with "?"');
  }

  // substring to strip "?" character
  const pairs = queryString.substring(1).split('&').map(segment => {
    const component = decodeURIComponent(segment);
    // we need to do this instead of using split to avoid splitting multiple times if a string has
    // more than 1 '=' i.e. string="sdfg==dsfg="
    const match = component.match(/=/);
    if (!match) {
      throw new Error('Please separate keys and values with the "=" character');
    }
    const param = component.substring(0, match.index);
    const valueString = component.substring(match.index + 1);

    return { param, valueString };
  }).filter(item => item);

  const parsedData = {};

  pairs.forEach(pair => {
    const { param, valueString } = pair;
    let parsedValue;
    const matchQuotes = valueString.match(/"(.*)"/);
    const matchBrackets = valueString.match(/\[(.*)\]/);
    if (matchQuotes) {
      parsedValue = matchQuotes[1];
    } else if (valueString === 'true') {
      parsedValue = true;
    } else if (valueString === 'false') {
      parsedValue = false;
    } else if (matchBrackets) {
      parsedValue = matchBrackets[1].split(',').map(num => Number(num));
    } else {
      parsedValue = Number(valueString);
    }

    parsedData[param] = parsedValue;
  });

  return parsedData;
};

